/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { auth } from '../firebase';
import {
    User
} from 'firebase/auth';
import { Task, UserProfile, TaskStatus, Team, Invitation, TeamAction, ExternalTask, BaseTask, TeamColorDef, IntegrationProviderInfo, Organization, AppNotification } from '../types';
import { useStandUpUI } from './useStandUpUI';
import { useIntegrationEngine } from './useIntegrationEngine';
import { useTeamManagement } from './useTeamManagement';
import { useTaskManagement } from './useTaskManagement';
import { useNotificationManagement } from './useNotificationManagement';
import { useInvitationManagement } from './useInvitationManagement';
import { useOrganizationManagement } from './useOrganizationManagement';
import { useOrganizationTeams } from './useOrganizationTeams';
import { useIdentityManagement } from './useIdentityManagement';
import { useTaskData } from './useTaskData';
import { useProfileManagement } from './useProfileManagement';
import { useAnalytics } from './useAnalytics'; // NEW
import { useUpdateEngine } from './useUpdateEngine';
import { IntegrationService } from '../services/IntegrationService';
import { TeamService } from '../services/TeamService';
import { UserService } from '../services/UserService';
import { AuthService } from '../services/AuthService';
import { OrganizationService } from '../services/OrganizationService';
import { isDesktop, isMobile, desktop } from '../platform';

// Derived UI logic
const isTauri = isDesktop();

interface StandUpContextType {
    user: User | null;
    userProfile: UserProfile | null;
    organization: Organization | null;
    organizations: Organization[]; // NEW: All organizations the user is part of
    myOrgTeams: Team[]; // NEW: Only teams in the active organization
    currentRole: 'manager' | 'engineer' | null;
    allUsers: UserProfile[];
    tasks: (Task | ExternalTask)[];
    externalTasks: ExternalTask[];
    activeProjectMap: Record<string, string>; // NEW: projectKey -> teamId
    externalIdentities: Record<string, any>;
    availableProviders: IntegrationProviderInfo[];
    team: Team | null; // Kept for legacy/single context, usually represents the "Primary" team
    notifications: AppNotification[]; // NEW: In-app alerts
    loading: boolean;
    isSettling: boolean; // NEW: To handle background migrations
    isInitializing: boolean; // NEW: To handle cold boot
    signIn: () => Promise<void>;
    logout: () => Promise<void>;

    markNotificationAsRead: (id: string) => Promise<void>;
    markAllNotificationsAsRead: () => Promise<void>;
    clearAllNotifications: () => Promise<void>;

    addTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    createExternalTask: (data: any) => Promise<void>;
    updateTask: (taskId: string, data: Partial<Task>) => Promise<void>;
    updateTaskStatus: (taskId: string, status: TaskStatus) => Promise<void>;
    deleteTask: (taskId: string) => Promise<void>;
    addTaskComment: (taskId: string, text: string) => Promise<void>;
    deleteTaskComment: (taskId: string, commentId: string) => Promise<void>;
    isTaskBlocked: (task: BaseTask) => boolean;
    getDependencies: (task: BaseTask) => (Task | ExternalTask)[];

    // Team Features
    createTeam: (name: string, visibility: 'public' | 'protected' | 'private') => Promise<void>;
    joinTeam: (teamId: string) => Promise<void>;
    leaveTeam: (teamId: string) => Promise<void>;
    deleteTeam: (teamId: string) => Promise<void>;
    sendInvitation: (teamId: string, teamName: string, inviteeId: string) => Promise<void>;
    inviteUserByEmail: (email: string, teamId: string, teamName: string) => Promise<void>;
    acceptInvitation: (invitation: Invitation) => Promise<void>;
    declineInvitation: (invitationId: string) => Promise<void>;
    invitations: Invitation[];
    allInvitations: Invitation[];
    emailInvites: Invitation[];
    switchTeam: (teamId: string) => Promise<void>; // Legacy: switches to single mode
    toggleTeam: (teamId: string) => Promise<void>; // New: toggles selection
    setAllTeams: (teamIds: string[]) => Promise<void>; // New: Select all
    updateMemberRole: (targetUserId: string, teamId: string, newRole: 'manager' | 'engineer') => Promise<void>;
    removeTeamMember: (targetUserId: string, teamId: string) => Promise<void>;
    deleteUserAccount: () => Promise<void>;
    fetchTeamMembers: (teamId: string) => Promise<UserProfile[]>;
    getTeamName: (teamId: string) => string;
    getTeamColor: (teamId: string) => TeamColorDef;
    getMyManagerTeamIds: () => string[]; // Helper for Manager View
    isManager: boolean;
    switchOrganization: (orgId: string) => Promise<void>; // NEW: Org Switcher
    createPersonalWorkspace: (displayName: string | null, email: string | null) => Promise<any>;
    acceptEmailInvite: (token: string) => Promise<any>;

    // Desktop Native Features
    isTauri: boolean;
    sendNativeNotification: (title: string, body: string) => Promise<void>;
    viewMode: 'full' | 'compact';
    view: 'manager' | 'engineer' | 'summary';
    setView: (view: 'manager' | 'engineer' | 'summary') => void;
    setViewMode: (mode: 'full' | 'compact') => void;
    toggleViewMode: () => Promise<void>;

    // Summary Persistence
    summaryDateRange: '1d' | '7d' | '30d' | 'all' | 'custom';
    setSummaryDateRange: (range: '1d' | '7d' | '30d' | 'all' | 'custom') => void;
    summaryStartDate: Date | null;
    setSummaryStartDate: (date: Date | null) => void;
    summaryEndDate: Date | null;
    setSummaryEndDate: (date: Date | null) => void;
    summaryAppliedRange: { start: number, end: number, version: number };
    setSummaryAppliedRange: React.Dispatch<React.SetStateAction<{ start: number, end: number, version: number }>>;

    // UI State
    isModalOpen: boolean;
    isTeamModalOpen: boolean;
    isMembersModalOpen: boolean;
    isTeamSettingsModalOpen: boolean;
    isForceUpdateOpen: boolean;
    isSettingsModalOpen: boolean;
    isDetailModalOpen: boolean;
    isMemberDetailOpen: boolean; // NEW
    viewingTeamMembersId: string | null;
    viewingTeamSettingsId: string | null;
    viewingDetailTask: Task | ExternalTask | null;
    viewingMemberId: string | null; // NEW
    orderedMemberIds: string[]; // NEW for navigation
    editingTask: Task | null;
    teamAction: TeamAction;

    // Update Engine
    updateStatus: 'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'uptodate' | 'error';
    updateError: string | null;
    availableUpdate: any;
    checkForUpdates: () => Promise<any>;
    installUpdate: () => Promise<void>;
    restartApp: () => Promise<void>;

    openModal: (task?: Task | ExternalTask) => void;
    closeModal: () => void;
    openTeamModal: (action: TeamAction) => void;
    closeTeamModal: () => void;
    openMembersModal: (teamId: string) => void;
    closeMembersModal: () => void;
    openTeamSettingsModal: (teamId: string) => void;
    closeTeamSettingsModal: () => void;
    openForceUpdateModal: () => void;
    closeForceUpdateModal: () => void;
    openSettingsModal: () => void;
    closeSettingsModal: () => void;
    openDetailModal: (task: Task | ExternalTask) => void;
    closeDetailModal: () => void;
    openMemberDetail: (userId: string, orderedIds?: string[]) => void; // NEW
    closeMemberDetail: () => void; // NEW
    toggleProviderSync: (provider: string, teamId: string) => Promise<void>;
    syncIntegrations: () => Promise<void>;
    updateExternalTaskStatus: (extTask: ExternalTask, newStatus: string, blockedReason?: string, newAssigneeId?: string, newTitle?: string, newDescription?: string) => Promise<void>;

    // Integration Service Methods
    fetchTeamProjectMappings: (teamId: string) => Promise<any[]>;
    fetchProjectMappingsForTeams: (orgId: string, teamIds: string[]) => Promise<any[]>;
    getExternalProjectList: (provider: string) => Promise<any[]>;

    // Team & User Service Methods
    listenToTeam: (teamId: string, callback: (team: Team | null) => void) => () => void;
    listenToPendingInvitations: (teamId: string, callback: (invitations: Invitation[]) => void) => () => void;
    listenToPendingEmailInvitations: (teamId: string, callback: (invitations: any[]) => void) => () => void;
    approveJoinRequest: (teamId: string, teamName: string, userId: string) => Promise<void>;
    declineJoinRequest: (teamId: string, userId: string) => Promise<void>;
    cancelEmailInvitation: (inviteId: string) => Promise<void>;
    getTeam: (teamId: string) => Promise<Team | null>;
    updateTeam: (teamId: string, data: Partial<Team>) => Promise<void>;
    isTeamNameDuplicate: (orgId: string, name: string) => Promise<boolean>;
    updateTeamProjectMappings: (teamId: string, provider: string, projectIds: string[], availableProjects: {id: string, name: string}[], orgId?: string) => Promise<void>;
    fetchDiscoverableTeams: (orgId: string, myTeamIds: string[]) => Promise<Team[]>;
    requestTeamAccess: (teamId: string, userId: string) => Promise<void>;
    fetchUsersByIds: (uids: string[]) => Promise<UserProfile[]>;

    // Organization Service Methods
    updateOrganization: (orgId: string, data: Partial<Organization>) => Promise<void>;

    toast: { message: string, type: 'success' | 'error' | 'info' } | null;
    showToast: (message: string, type?: 'success' | 'error' | 'info') => void;

    // Analytics (Agnostic)
    track: (eventName: string, properties?: any) => void;
    identify: (userId: string, traits?: any) => void;
    page: (pageName?: string, properties?: any) => void;
}

const StandUpContext = createContext<StandUpContextType | undefined>(undefined);

const TEAM_COLORS: TeamColorDef[] = [
    { style: 'bg-blue-100 text-blue-800 dark:bg-blue-900/80 dark:text-blue-200 border-blue-200 dark:border-blue-800', dot: 'bg-blue-500' },
    { style: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/80 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800', dot: 'bg-emerald-500' },
    { style: 'bg-purple-100 text-purple-800 dark:bg-purple-900/80 dark:text-purple-200 border-purple-200 dark:border-purple-800', dot: 'bg-purple-500' },
    { style: 'bg-amber-100 text-amber-800 dark:bg-amber-900/80 dark:text-amber-200 border-amber-200 dark:border-amber-800', dot: 'bg-amber-500' },
    { style: 'bg-pink-100 text-pink-800 dark:bg-pink-900/80 dark:text-pink-200 border-pink-200 dark:border-pink-800', dot: 'bg-pink-500' },
    { style: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/80 dark:text-cyan-200 border-cyan-200 dark:border-cyan-800', dot: 'bg-cyan-500' },
    { style: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/80 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800', dot: 'bg-indigo-500' },
    { style: 'bg-rose-100 text-rose-800 dark:bg-rose-900/80 dark:text-rose-200 border-rose-200 dark:border-rose-800', dot: 'bg-rose-500' },
];

export const StandUpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSettling, setIsSettling] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);

    const {
        userProfile,
        setUserProfile
    } = useProfileManagement({ user, setLoading, setIsSettling });

    const { track, identify, page } = useAnalytics(userProfile);

    const {
        viewMode, setViewMode, toggleViewMode,
        view, setView,
        isModalOpen, openModal: baseOpenModal, closeModal: baseCloseModal,
        isTeamModalOpen, openTeamModal, closeTeamModal,
        isMembersModalOpen, openMembersModal, closeMembersModal,
        isTeamSettingsModalOpen, openTeamSettingsModal, closeTeamSettingsModal,
        isForceUpdateOpen, openForceUpdateModal, closeForceUpdateModal,
        isSettingsModalOpen, openSettingsModal, closeSettingsModal,
        isDetailModalOpen, openDetailModal, closeDetailModal,
        isMemberDetailOpen, openMemberDetail, closeMemberDetail,
        viewingTeamMembersId,
        viewingTeamSettingsId,
        viewingDetailTask,
        viewingMemberId,
        orderedMemberIds,
        editingTask,
        teamAction,
        summaryDateRange, setSummaryDateRange,
        summaryStartDate, setSummaryStartDate,
        summaryEndDate, setSummaryEndDate,
        summaryAppliedRange, setSummaryAppliedRange,
        toast, showToast
    } = useStandUpUI(track);

    // DOMAIN LOGIC: Wrap openModal to enforce team-check
    const openModal = (task?: Task | ExternalTask) => {
        if (!task && myOrgTeams.length === 0) {
            showToast("Please create a team first before adding tasks.", "info");
            return;
        }
        baseOpenModal(task);
    };

    const closeModal = () => baseCloseModal();

    const {
        updateStatus, updateError, availableUpdate, checkForUpdates, installUpdate, restartApp
    } = useUpdateEngine({ isTauri, showToast, openForceUpdateModal });

    // Derived State
    const activeTeamIds = userProfile?.activeTeamIds || [];
    
    // SECURITY REFINEMENT: effectiveTeamIds MUST be filtered by the active orgId.
    // This prevents stale team IDs from other organizations from being used 
    // during the few milliseconds of an Org Switch race condition.
    const effectiveTeamIds = useMemo(() => {
        if (!userProfile?.orgId) return [];
        
        // 1. Get all IDs from active selection
        const baseIds = activeTeamIds.length > 0
            ? activeTeamIds
            : (userProfile?.activeTeamId ? [userProfile.activeTeamId] : []);

        // 2. Filter: Only keep IDs that belong to the active organization's membership list
        return baseIds.filter(id =>
            userProfile.teams?.some(t => t.teamId === id)
        );
    }, [userProfile?.orgId, activeTeamIds, JSON.stringify(userProfile?.teams)]);

    const handleUserSyncBridge = async (firebaseUser: User) => {
        setIsSettling(true);
        setUserProfile(null);
        await AuthService.handleUserSync(firebaseUser);
    };

    const logout = async () => {
        setLoading(true); // Re-enter loading state during logout
        await AuthService.logout();
        setUserProfile(null);
    };
    // Identify user for analytics when profile is loaded
    useEffect(() => {
        if (userProfile) {
            identify(userProfile.id, {
                email: userProfile.email,
                displayName: userProfile.displayName,
                orgId: userProfile.orgId
            });
        }
    }, [userProfile, identify]);

    const {
        createExternalTask,
        syncIntegrations,
        toggleProviderSync,
        updateExternalTaskStatus
    } = useIntegrationEngine({ user, userProfile, setUserProfile, showToast, track });

    const { notifications, markNotificationAsRead, markAllNotificationsAsRead, clearAllNotifications } = useNotificationManagement({ user, userProfile });
    const { invitations, allInvitations, emailInvites } = useInvitationManagement({ user, userProfile });
    const { organization, organizations, updateOrganization } = useOrganizationManagement({ userProfile, track });
    const { allUsers, myOrgTeams, team } = useOrganizationTeams({ userProfile, effectiveTeamIds });
    const { externalIdentities, availableProviders } = useIdentityManagement({ userProfile });
    const { tasks, externalTasks, activeProjectMap } = useTaskData({ user, userProfile, effectiveTeamIds });

    const { 
        createTeam, joinTeam, leaveTeam, deleteTeam, sendInvitation, inviteUserByEmail, acceptInvitation, declineInvitation,
        switchTeam, toggleTeam, setAllTeams, updateMemberRole, removeTeamMember,
        deleteUserAccount, fetchTeamMembers, getTeamName, getTeamColor, getMyManagerTeamIds
    } = useTeamManagement({ 
        user, 
        userProfile, 
        allUsers, 
        team, 
        effectiveTeamIds, 
        teamColors: TEAM_COLORS,
        logout,
        setLoading,
        track
    });

    const {
        addTask, updateTask, updateTaskStatus, deleteTask,
        addTaskComment, deleteTaskComment,
        getDependencies, isTaskBlocked
    } = useTaskManagement({
        userProfile,
        tasks: [...tasks, ...externalTasks],
        externalTasks,
        showToast,
        track,
        updateExternalTaskStatus
    });

    // Primary role is strictly for UI adaptation. 
    // We only look at roles within teams that belong to the ACTIVE organization.
    const currentRole = useMemo(() => {
        if (!userProfile?.orgId || effectiveTeamIds.length === 0) return 'engineer';
        
        const activeTeamMembership = userProfile.teams?.find(t => effectiveTeamIds.includes(t.teamId));
        return activeTeamMembership?.role || 'engineer';
    }, [userProfile?.orgId, effectiveTeamIds, JSON.stringify(userProfile?.teams)]);

    // Auth & Profile Listener
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser: any) => {
            setUser(currentUser);
            if (!currentUser) {
                setUserProfile(null);
                setLoading(false);
            } else {
                // IMPORTANT: Do NOT set loading to false here. 
                // Wait for the Profile Listener to finish.
                setLoading(true);
            }
            // Once the FIRST auth check is done, we are no longer initializing.
            setIsInitializing(false);
        });
        return unsubscribe;
    }, []);

    const signIn = async () => {
        console.log(`[Auth] Triggering sign-in (isDesktop: ${isDesktop()})`);
        if (isDesktop()) {
            return await AuthService.tauriSignIn(async (u) => { 
                await handleUserSyncBridge(u); 
            });
        }
        if (isMobile()) {
            const loggedInUser = await AuthService.nativeSignIn();
            await handleUserSyncBridge(loggedInUser);
            return;
        }
        try {
            const result = await AuthService.signInWithGoogle();
            await handleUserSyncBridge(result);
        } catch (error: any) {
            alert("Sign-in error: " + error.message);
        }
    };

    const switchOrganization = async (newOrgId: string) => {
        if (!user || !userProfile || userProfile.orgId === newOrgId) return;
        setLoading(true); // Re-enter loading state for smooth transition
        try {
            await OrganizationService.switchOrganization(user.uid, newOrgId);
            console.log(`[Org] User ${user.uid} switched to organization ${newOrgId}.`);
        } catch (e) {
            console.error("[Org Error] Failed to switch organization:", e);
            showToast("Failed to switch organization", "error");
            setLoading(false); // Only reset on error, listener handles success
        }
    };

    const sendNativeNotification = async (title: string, body: string) => {
        if (isDesktop()) {
            await desktop.sendNotification(title, body);
        } else if ('Notification' in window) {
            if (Notification.permission === 'granted') {
                new Notification(title, { body });
            } else if (Notification.permission !== 'denied') {
                Notification.requestPermission().then(permission => {
                    if (permission === 'granted') {
                        new Notification(title, { body });
                    }
                });
            }
        }
    };

    const isManager = currentRole === 'manager';

    return (
        <StandUpContext.Provider value={{
            user, userProfile, organization, organizations, myOrgTeams, currentRole, isManager, allUsers, 
            tasks: [...tasks, ...externalTasks], // Unified list
            externalTasks,
            activeProjectMap,
            externalIdentities,
            availableProviders,
            team, notifications, invitations, allInvitations, emailInvites, loading, isSettling, isInitializing, signIn, logout,
            markNotificationAsRead, markAllNotificationsAsRead, clearAllNotifications,
            createTeam, joinTeam, leaveTeam, deleteTeam, sendInvitation, inviteUserByEmail, acceptInvitation, declineInvitation, 
            switchTeam, toggleTeam, setAllTeams, updateMemberRole, removeTeamMember, deleteUserAccount, fetchTeamMembers, getTeamName, getTeamColor, getMyManagerTeamIds, switchOrganization,
            createPersonalWorkspace: AuthService.createPersonalWorkspace,
            acceptEmailInvite: AuthService.acceptEmailInvite,
            isTauri, sendNativeNotification, viewMode, setViewMode, toggleViewMode, view, setView, addTask, createExternalTask, updateTask, updateTaskStatus, deleteTask, addTaskComment, deleteTaskComment,
            summaryDateRange, setSummaryDateRange,
            summaryStartDate, setSummaryStartDate,
            summaryEndDate, setSummaryEndDate,
            summaryAppliedRange, setSummaryAppliedRange,
            updateStatus, updateError, availableUpdate, checkForUpdates, installUpdate, restartApp,
            isTaskBlocked, getDependencies, isModalOpen, isTeamModalOpen, isMembersModalOpen, isTeamSettingsModalOpen, isForceUpdateOpen, isSettingsModalOpen, isDetailModalOpen, isMemberDetailOpen, viewingTeamMembersId, viewingTeamSettingsId, viewingDetailTask, viewingMemberId, orderedMemberIds, editingTask, teamAction,
            openModal, closeModal, openTeamModal, closeTeamModal, openMembersModal, closeMembersModal, openTeamSettingsModal, closeTeamSettingsModal, openForceUpdateModal, closeForceUpdateModal, openSettingsModal, closeSettingsModal, openDetailModal, closeDetailModal, openMemberDetail, closeMemberDetail, toggleProviderSync, syncIntegrations, updateExternalTaskStatus,
            fetchTeamProjectMappings: IntegrationService.fetchTeamProjectMappings,
            fetchProjectMappingsForTeams: IntegrationService.fetchProjectMappingsForTeams,
            getExternalProjectList: IntegrationService.getExternalProjectList,
            listenToTeam: TeamService.listenToTeam,
            listenToPendingInvitations: TeamService.listenToPendingInvitations,
            listenToPendingEmailInvitations: TeamService.listenToPendingEmailInvitations,
            approveJoinRequest: TeamService.approveJoinRequest,
            declineJoinRequest: TeamService.declineJoinRequest,
            cancelEmailInvitation: TeamService.cancelEmailInvitation,
            getTeam: TeamService.getTeam,
            updateTeam: TeamService.updateTeam,
            isTeamNameDuplicate: TeamService.isTeamNameDuplicate,
            updateTeamProjectMappings: TeamService.updateTeamProjectMappings,
            fetchDiscoverableTeams: TeamService.fetchDiscoverableTeams,
            requestTeamAccess: TeamService.requestTeamAccess,
            fetchUsersByIds: UserService.fetchUsersByIds,
            updateOrganization,
            track, identify, page,
            toast, showToast
        }}>
            {children}
        </StandUpContext.Provider>
    );
};

export const useStandUp = () => {
    const context = useContext(StandUpContext);
    if (!context) throw new Error("useStandUp must be used within StandUpProvider");
    return context;
};
